import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Search, Building2, MapPinPlus, X } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { Input } from '../common';
import { useStructuresStore } from '../../store/structuresStore';
import {
  placeAutocomplete,
  placeDetails,
  newSessionToken,
  type PlaceSuggestion,
} from '../../utils/geocode';
import type { Structure } from '../../types';

interface Props {
  onPick: (structure: Structure) => void;
}

/**
 * Address-search + result list for picking a structure during post creation.
 * Owns its own query state; calls back to the parent with the chosen result.
 *
 * Two sources: REDI's own seeded/user-added structures (instant, by id) and
 * Google Places for any address not yet in the dataset. Picking a Places
 * result creates an unclaimed user-added structure on the fly (claim:false —
 * attaching an observation must not claim the property) and links the post to
 * it.
 */
export function StructurePickerSection({ onPick }: Props) {
  const { colors } = useTheme();
  const {
    searchResults,
    isSearching,
    searchStructures,
    clearSearch,
    addUserStructure,
    getStructureById,
  } = useStructuresStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [placeSuggestions, setPlaceSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One Places session token per search session (reset after a pick) so
  // autocomplete + details bill as a single session.
  const sessionTokenRef = useRef<string>(newSessionToken());
  const placesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback(
    (text: string) => {
      setSearchQuery(text);
      setError(null);
      const q = text.trim();

      // Seeded/user-added structures (local, cheap — no debounce).
      if (q.length >= 3) searchStructures(q);
      else clearSearch();

      // Google Places (debounced).
      if (placesDebounceRef.current) clearTimeout(placesDebounceRef.current);
      if (q.length >= 3) {
        placesDebounceRef.current = setTimeout(async () => {
          const found = await placeAutocomplete(q, sessionTokenRef.current);
          setPlaceSuggestions(found);
        }, 250);
      } else {
        setPlaceSuggestions([]);
      }
    },
    [searchStructures, clearSearch]
  );

  const resetAfterPick = useCallback(() => {
    setSearchQuery('');
    setPlaceSuggestions([]);
    clearSearch();
    sessionTokenRef.current = newSessionToken();
  }, [clearSearch]);

  const handleClear = useCallback(() => {
    setSearchQuery('');
    setPlaceSuggestions([]);
    setError(null);
    clearSearch();
  }, [clearSearch]);

  const handleSelect = useCallback(
    (structure: Structure) => {
      onPick(structure);
      resetAfterPick();
    },
    [onPick, resetAfterPick]
  );

  const handleCreateFromPlace = useCallback(
    async (s: PlaceSuggestion) => {
      setError(null);
      setIsCreating(true);
      try {
        const details = await placeDetails(s.placeId, sessionTokenRef.current);
        if (!details) {
          setError("Couldn't load that address. Try another.");
          return;
        }
        const result = await addUserStructure({
          address: details.street || details.formattedAddress,
          city: details.city,
          state: details.state,
          zip: details.zip || undefined,
          centroid: details.centroid,
          claim: false,
        });
        const created = await getStructureById(result.structureId);
        if (!created) {
          setError('Address added, but it could not be loaded. Try searching for it again.');
          return;
        }
        onPick(created);
        resetAfterPick();
      } catch (e) {
        const msg = e instanceof Error ? e.message : '';
        setError(
          /anonymous|account|sign/i.test(msg)
            ? 'Sign in to add an address that is not on the map yet.'
            : "Couldn't add that address. Please try again."
        );
      } finally {
        setIsCreating(false);
      }
    },
    [addUserStructure, getStructureById, onPick, resetAfterPick]
  );

  useEffect(
    () => () => {
      if (placesDebounceRef.current) clearTimeout(placesDebounceRef.current);
    },
    []
  );

  const placeRows = placeSuggestions.slice(0, 4);
  const showResults = searchResults.length > 0 || placeRows.length > 0;

  return (
    <View style={styles.section}>
      <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
        Where is this observation?
      </Text>
      <View
        style={[
          styles.searchContainer,
          // Canvas + hairline, matching the shared Input this wraps. The
          // wrapper survives only because the fused dropdown needs square
          // bottom corners, which Input doesn't expose; it must not add a fill.
          { backgroundColor: colors.background, borderColor: colors.border },
          // Square the bottom corners so the open dropdown fuses to the input.
          showResults && styles.searchContainerOpen,
        ]}>
        <Search size={16} color={colors.textTertiary} />
        <Input
          placeholder="Search your address..."
          value={searchQuery}
          onChangeText={handleSearch}
          containerStyle={styles.searchInput}
          style={styles.searchInputText}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity
            onPress={handleClear}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            hitSlop={8}
            style={styles.clearButton}>
            <X size={16} color={colors.textTertiary} />
          </TouchableOpacity>
        )}
      </View>
      {/* Only when the dropdown is closed — keep results visually attached to
          the input. */}
      {!showResults && (
        // textSecondary, not textTertiary: instructions, not a placeholder —
        // textTertiary is sub-AA by design (audit:a11y).
        <Text style={[styles.helper, { color: colors.textSecondary }]}>
          Find the property this is about. Picking it attaches your post and sets the location.
        </Text>
      )}

      {showResults && (
        <View style={[styles.searchResults, { borderColor: colors.border }]}>
          {searchResults.slice(0, 5).map((structure) => (
            <TouchableOpacity
              key={structure.id}
              onPress={() => handleSelect(structure)}
              style={[styles.searchResultItem, { borderBottomColor: colors.border }]}
              activeOpacity={0.7}>
              <Building2 size={16} color={colors.textSecondary} />
              <View style={styles.searchResultText}>
                <Text
                  style={[styles.searchResultAddress, { color: colors.text }]}
                  numberOfLines={1}>
                  {structure.address || 'Unknown address'}
                </Text>
                <Text
                  style={[styles.searchResultCity, { color: colors.textTertiary }]}
                  numberOfLines={1}>
                  {structure.city}, {structure.state}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
          {/* Google Places results for addresses not yet in REDI. Picking one
              creates an unclaimed user-added structure and links the post. */}
          {placeRows.map((s) => (
            <TouchableOpacity
              key={s.placeId}
              onPress={() => handleCreateFromPlace(s)}
              disabled={isCreating}
              style={[styles.searchResultItem, { borderBottomColor: colors.border }]}
              activeOpacity={0.7}>
              <MapPinPlus size={16} color={colors.text} />
              <View style={styles.searchResultText}>
                <Text
                  style={[styles.searchResultAddress, { color: colors.text }]}
                  numberOfLines={1}>
                  {s.primaryText}
                </Text>
                <Text
                  style={[styles.searchResultCity, { color: colors.textTertiary }]}
                  numberOfLines={1}>
                  {s.secondaryText || 'Add this address'}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {isCreating && (
        <Text style={[styles.searchingText, { color: colors.textTertiary }]}>
          Adding address...
        </Text>
      )}
      {!!error && <Text style={[styles.searchingText, { color: colors.danger }]}>{error}</Text>}
      {isSearching && !isCreating && (
        <Text style={[styles.searchingText, { color: colors.textTertiary }]}>Searching...</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: { marginBottom: SPACING.md },
  sectionLabel: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.sm,
  },
  // Matches Input's own default field (padding 14 vertical, radius
  // BORDER_RADIUS.lg) — Input itself can't be reused directly here since it
  // only supports a left icon OR a clear button, and this field needs both
  // (Sept 29 2026).
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    paddingLeft: SPACING.smd,
    gap: SPACING.sm,
  },
  searchContainerOpen: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  searchInput: { flex: 1, marginBottom: 0 },
  // paddingLeft 0: the container's paddingLeft + icon + gap already inset the
  // text; keeping the Input's own 16px pushed the placeholder ~48px in.
  searchInputText: {
    borderWidth: 0,
    backgroundColor: 'transparent',
    paddingLeft: 0,
    paddingVertical: 14,
  },
  clearButton: { paddingRight: SPACING.smd, paddingLeft: SPACING.xs },
  helper: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xs,
  },
  searchResults: {
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: BORDER_RADIUS.md,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    overflow: 'hidden',
    // Overlap the input's bottom border by 1px so they read as one box.
    marginTop: -1,
  },
  searchResultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: SPACING.smd,
    gap: SPACING.sm,
    borderBottomWidth: 1,
  },
  searchResultText: { flex: 1 },
  searchResultAddress: {
    ...TYPOGRAPHY.label,
  },
  searchResultCity: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  searchingText: {
    ...TYPOGRAPHY.bodySmall,
    textAlign: 'center',
    marginTop: SPACING.sm,
  },
});
